import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import {
  LayoutGrid,
  FileCheck,
  TrendingUp,
  Shield,
  Cloud,
  Workflow,
  Menu,
  X,
  ChevronRight,
  CheckCircle,
  Calendar,
  Users,
  DollarSign,
  ArrowRight,
  Phone,
  Mail,
  MapPin } from
"lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"./components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Component - White version for dark backgrounds
const Logo = ({ className = "", size = "default", isScrolled = false }) => {
  // White logo icon for navigation (changes when scrolled)
  if (size === "nav-icon") {
    return (
      <img
        src="https://customer-assets.emergentagent.com/job_synergy-system/artifacts/9d4l2bkq_LOGO%20PNG-04-04.png"
        alt="Synergy DDS"
        className={`h-8 w-auto transition-all duration-300 ${isScrolled ? '' : 'brightness-0 invert'} ${className}`}
        data-testid="logo-icon" />);


  }

  // Large watermark for hero background
  if (size === "watermark") {
    return (
      <img
        src="https://customer-assets.emergentagent.com/job_synergy-system/artifacts/9d4l2bkq_LOGO%20PNG-04-04.png"
        alt=""
        className={`brightness-0 invert opacity-[0.12] ${className}`}
        aria-hidden="true" />);


  }

  // Default small logo (white)
  return (
    <img
      src="https://customer-assets.emergentagent.com/job_synergy-system/artifacts/9d4l2bkq_LOGO%20PNG-04-04.png"
      alt="Synergy DDS"
      className={`h-10 w-auto brightness-0 invert ${className}`}
      data-testid="logo" />);


};





// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
  { href: "#problem", label: "The Challenge" },
  { href: "#system", label: "Our System" },
  { href: "#revenue", label: "Your Revenue" },
  { href: "#technology", label: "Technology" },
  { href: "#contact", label: "Contact" }];


  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isScrolled ?
      'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100' :
      'bg-transparent'}`
      }
      data-testid="navigation">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#hero" onClick={() => scrollToSection('#hero')} data-testid="nav-logo" className="flex items-center gap-3">
            <Logo size="nav-icon" isScrolled={isScrolled} />
            <span className={`font-manrope font-bold text-xl transition-colors duration-300 ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
              Synergy DDS
            </span>
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
            <button
              key={link.href}
              onClick={() => scrollToSection(link.href)}
              className={`nav-link font-medium text-sm whitespace-nowrap ${
              isScrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'}`
              }
              data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}>

                {link.label}
              </button>
            )}
            <Button
              onClick={() => scrollToSection('#contact')}
              className="btn-primary text-white rounded-full px-6 py-2"
              data-testid="nav-cta-button">

              Partner With Us
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button">

            {isMobileMenuOpen ?
            <X className={isScrolled ? 'text-slate-900' : 'text-white'} size={24} /> :

            <Menu className={isScrolled ? 'text-slate-900' : 'text-white'} size={24} />
            }
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen &&
        <div className="md:hidden bg-white rounded-2xl shadow-xl mt-2 p-6 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) =>
            <button
              key={link.href}
              onClick={() => scrollToSection(link.href)}
              className="text-slate-600 hover:text-slate-900 font-medium py-2 text-left"
              data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}>

                  {link.label}
                </button>
            )}
              <Button
              onClick={() => scrollToSection('#contact')}
              className="btn-primary text-white rounded-full w-full mt-4"
              data-testid="mobile-nav-cta-button">

                Partner With Us
              </Button>
            </div>
          </div>
        }
      </div>
    </nav>);

};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center bg-slate-950"
      data-testid="hero-section">

      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1626726183219-e230165db244?crop=entropy&cs=srgb&fm=jpg&q=85')`
        }} />

      <div className="absolute inset-0 hero-gradient" />
      
      {/* Watermark Logo - Large bottom-left positioned, bleeding off edges */}
      <div className="absolute bottom-0 left-0 pointer-events-none overflow-hidden" style={{ width: '160vw', height: '160vh' }}>
        <Logo size="watermark" className="absolute -bottom-[25%] -left-[20%] w-full h-full object-contain" />
      </div>
      
      {/* Content - Right aligned for balance */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl ml-auto text-right">
          <p className="text-sky-400 font-semibold text-sm uppercase tracking-wider mb-4 animate-fade-in-up">Premier HUMAN Remote Operations

          </p>
          <h1 className="font-manrope text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in-up animation-delay-100">
            The Operational Engine for the{' '}
            <span className="text-sky-400">Itinerant Specialist</span>
          </h1>
          <p className="sm:text-xl ml-auto max-w-2xl animate-fade-in-up animation-delay-200 !text-2xl mb-8 text-slate-300">Expert HUMAN remote patient coordination and logistics for traveling surgical and sedation practices.

            <span className="text-white font-medium"> 6+ years of experience maximizing specialist production.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300 justify-end">
            <Button
              onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary text-white rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="hero-cta-primary">

              Partner with Synergy DDS
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              variant="outline"
              onClick={() => document.querySelector('#system').scrollIntoView({ behavior: 'smooth' })}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg font-medium"
              data-testid="hero-cta-secondary">

              Explore Our System
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2" />
        </div>
      </div>
    </section>);

};

// Problem/Solution Section
const ProblemSolutionSection = () => {
  return (
    <section id="problem" className="py-20 md:py-32 bg-white" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <div>
            <p className="text-sky-500 font-semibold text-sm uppercase tracking-wider mb-4">
              The Challenge
            </p>
            <h2 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              The Itinerant Paradox
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              The more you travel, the harder it becomes to manage. Every new host office means 
              another set of protocols, another patient population, another scheduling system to navigate.
            </p>
            
            <div className="space-y-4 mb-8">
              {[
              "Fragmented communication across multiple offices",
              "Lost revenue from preventable cancellations",
              "Constant friction with host office staff",
              "Exhausting administrative overhead"].
              map((item, index) =>
              <div key={index} className="flex items-start gap-3">
                  <X className="text-red-400 mt-1 flex-shrink-0" size={20} />
                  <p className="!my-[2px] !mx-[2px] !py-[10px] !px-[2px] text-slate-600">{item}</p>
                </div>
              )}
            </div>

            <div className="bg-slate-50 rounded-2xl p-6 border-l-4 border-sky-500">
              <h3 className="font-manrope text-xl font-semibold text-slate-900 mb-2">
                The Synergy Solution
              </h3>
              <p className="!text-2xl text-slate-600">
                We become your <span className="font-semibold text-slate-900">"Fixed Front Office"</span> — 
                a dedicated operations team that travels with you digitally, eliminating host-office friction 
                and maximizing every circuit day.
              </p>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1766066014237-00645c74e9c6?auto=format&fit=crop&w=800&q=80"
                alt="Friendly front office coordinator with headset"
                className="w-full h-auto" />

            </div>
            {/* Stats Card */}
            <div className="absolute -bottom-6 -left-6 border border-slate-100 p-6 !rounded-[25px] !shadow-2xl bg-white">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="text-sky-500" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">6+ Years</p>
                  <p className="text-slate-500 text-sm">Maximizing Production</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Synergy System Section
const SynergySystemSection = () => {
  const steps = [
  {
    number: "01",
    title: "Centralize",
    icon: LayoutGrid,
    description: "Google Workspace integration and Master Calendar sync across all your host offices.",
    details: [
    "Unified scheduling platform",
    "Real-time calendar synchronization",
    "Multi-office visibility"]

  },
  {
    number: "02",
    title: "Prepare",
    icon: FileCheck,
    description: "Direct patient outreach, NPO vetting, and host-office record coordination.",
    details: [
    "Pre-operative patient calls",
    "Medical history verification",
    "Documentation preparation"]

  },
  {
    number: "03",
    title: "Produce",
    icon: TrendingUp,
    description: "Maximizing density and eliminating cancellations for peak production days.",
    details: [
    "Schedule optimization",
    "Same-day fill protocols",
    "Revenue maximization"]

  }];


  return (
    <section id="system" className="py-20 md:py-32 bg-slate-50" data-testid="system-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-sky-500 font-semibold text-sm uppercase tracking-wider mb-4">
            Our Process
          </p>
          <h2 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            The Synergy System
          </h2>
          <p className="text-slate-600 text-lg">
            A proven three-step methodology that transforms your traveling practice 
            into a well-oiled operational machine.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) =>
          <div
            key={index}
            className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 card-hover"
            data-testid={`system-step-${index + 1}`}>

              <div className="flex items-center justify-between mb-6">
                <span className="step-number text-5xl font-bold">{step.number}</span>
                <div className="w-14 h-14 icon-container rounded-xl flex items-center justify-center">
                  <step.icon className="text-sky-500" size={28} />
                </div>
              </div>
              <h3 className="font-manrope text-2xl font-bold text-slate-900 mb-3">
                {step.title}
              </h3>
              <p className="text-slate-600 mb-6">
                {step.description}
              </p>
              <ul className="space-y-3">
                {step.details.map((detail, idx) =>
              <li key={idx} className="flex items-center gap-3">
                    <CheckCircle className="text-sky-500 flex-shrink-0" size={18} />
                    <span className="text-slate-600 text-sm">{detail}</span>
                  </li>
              )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Revenue Section
const RevenueSection = () => {
  return (
    <section id="revenue" className="py-20 md:py-32 bg-slate-900" data-testid="revenue-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div>
            <p className="text-sky-400 font-semibold text-sm uppercase tracking-wider mb-4">
              Your Revenue
            </p>
            <h2 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Keep <span className="text-sky-400">100%</span> of Your Increased Revenue
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-8">
              Unlike percentage-based services that eat into your earnings, we charge a 
              <span className="text-white font-semibold"> simple flat monthly fee</span>. 
              Every dollar of increased production goes directly to you.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <DollarSign className="text-sky-400 mb-4" size={32} />
                <h4 className="font-manrope text-xl font-semibold text-white mb-2">
                  Flat Monthly Fee
                </h4>
                <p className="text-slate-400 text-sm">
                  Predictable costs, predictable growth. No surprises on your statement.
                </p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <TrendingUp className="text-sky-400 mb-4" size={32} />
                <h4 className="font-manrope text-xl font-semibold text-white mb-2">
                  Pure Profit Growth
                </h4>
                <p className="text-slate-400 text-sm">
                  Your revenue increases. Our fee stays the same. Simple math.
                </p>
              </div>
            </div>

            <Button
              onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary text-white rounded-full px-8 py-6 text-lg font-semibold"
              data-testid="revenue-cta-button">

              Optimize Your Circuit
              <ChevronRight className="ml-2" size={20} />
            </Button>
          </div>

          {/* Stats */}
          <div className="relative">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 md:p-12 border border-slate-700">
              <div className="text-center mb-8">
                <p className="md:text-8xl stat-highlight !font-bold !text-6xl mb-2 text-white">0% OVERHEAD</p>
                <p className="text-slate-400 text-lg"></p>
              </div>
              <div className="section-divider mb-8" />
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <p className="md:text-4xl !font-bold !text-9xl mb-1 text-sky-400">NO </p>
                  <p className="text-slate-400 text-sm"></p>
                </div>
                <div className="text-center">
                  <p className="md:text-4xl text-3xl font-bold !mb-[4px] !mr-[36px] !text-[#FBFBFB]">
                    RENT.<br />
                    EMPLOYEE TAXES.<br />
                    UTILITIES.
                  </p>
                  <p className="text-slate-400 text-sm"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);};
// Technology Section
const TechnologySection = () => {
  const features = [
  {
    icon: Cloud,
    title: "Google Workspace Powered",
    description: "Enterprise-grade infrastructure you already trust, seamlessly integrated into your workflow."
  },
  {
    icon: Shield,
    title: "HIPAA Compliant",
    description: "Bank-level security protocols protect patient data across all touchpoints."
  },
  {
    icon: Workflow,
    title: "Platform Agnostic",
    description: "Works alongside any practice management software — Dentrix, Eaglesoft, Open Dental, and more."
  },
  {
    icon: Calendar,
    title: "Real-Time Sync",
    description: "Instant calendar updates across all your host offices, visible from anywhere."
  },
  {
    icon: Users,
    title: "Multi-Office Coordination",
    description: "Centralized communication hub for all your referring relationships."
  },
  {
    icon: CheckCircle,
    title: "Automated Workflows",
    description: "Systematic patient preparation reduces no-shows and maximizes chair time."
  }];


  return (
    <section id="technology" className="py-20 md:py-32 bg-white" data-testid="technology-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start mb-16">
          <div>
            <p className="text-sky-500 font-semibold text-sm uppercase tracking-wider mb-4">
              Technology
            </p>
            <h2 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900">
              Secure, Platform-Agnostic Operations
            </h2>
          </div>
          <div>
            <p className="text-slate-600 text-lg leading-relaxed">Powered by Google Workspace
- No software to install, no learning curve, just seamless operational support.


            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) =>
          <div
            key={index}
            className="bg-slate-50 rounded-2xl p-6 border border-slate-100 card-hover"
            data-testid={`tech-feature-${index + 1}`}>

              <div className="w-12 h-12 icon-container rounded-xl flex items-center justify-center mb-4">
                <feature.icon className="text-sky-500" size={24} />
              </div>
              <h3 className="font-manrope text-lg font-semibold text-slate-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-slate-600 text-sm">
                {feature.description}
              </p>
            </div>
          )}
        </div>

        {/* Image Row */}
        <div className="mt-16 rounded-2xl overflow-hidden shadow-lg">
          <img
            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1600&q=80"
            alt="Modern technology workspace"
            className="w-full h-64 md:h-80 object-cover" />

        </div>
      </div>
    </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practice_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, practice_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setFormData({
        name: '',
        email: '',
        phone: '',
        practice_type: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Content */}
          <div>
            <p className="text-sky-500 font-semibold text-sm uppercase tracking-wider mb-4">
              Get Started
            </p>
            <h2 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Partner with Synergy DDS
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              Ready to transform your itinerant practice? Let's discuss how our operational 
              support can maximize your production and eliminate administrative friction.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <Mail className="text-sky-500" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Email Us</p>
                  <p className="text-slate-900 font-medium">dsotomayor@synergydds.net</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <Phone className="text-sky-500" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Call Us</p>
                  <p className="text-slate-900 font-medium">(818)254-8353</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <MapPin className="text-sky-500" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Location</p>
                  <p className="text-slate-900 font-medium">Serving specialists nationwide</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-700 font-medium text-sm mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Dr. John Smith"
                    required
                    className="h-12"
                    data-testid="contact-name-input" />

                </div>
                <div>
                  <label className="block text-slate-700 font-medium text-sm mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="doctor@practice.com"
                    required
                    className="h-12"
                    data-testid="contact-email-input" />

                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-700 font-medium text-sm mb-2">
                    Phone
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="h-12"
                    data-testid="contact-phone-input" />

                </div>
                <div>
                  <label className="block text-slate-700 font-medium text-sm mb-2">
                    Practice Type *
                  </label>
                  <Select value={formData.practice_type} onValueChange={handleSelectChange} required>
                    <SelectTrigger className="h-12" data-testid="contact-practice-select">
                      <SelectValue placeholder="Select specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oral-surgery">Oral Surgery</SelectItem>
                      <SelectItem value="periodontics">Periodontics</SelectItem>
                      <SelectItem value="dental-anesthesiology">Dental Anesthesiology</SelectItem>
                      <SelectItem value="endodontics">Endodontics</SelectItem>
                      <SelectItem value="other">Other Specialty</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-medium text-sm mb-2">
                  Message *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your practice and how we can help optimize your circuit..."
                  required
                  rows={4}
                  className="resize-none"
                  data-testid="contact-message-input" />

              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary text-white rounded-full w-full py-6 text-lg font-semibold"
                data-testid="contact-submit-button">

                {isSubmitting ? 'Sending...' : 'Send Message'}
                {!isSubmitting && <ArrowRight className="ml-2" size={20} />}
              </Button>

              <p className="text-slate-500 text-sm text-center">
                We typically respond within 24 hours.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <Logo className="mb-6" />
            <p className="text-slate-400 mb-6 max-w-md">
              The operational engine for itinerant dental specialists. 
              Expert remote patient coordination and logistics for traveling practices.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-manrope text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {['The Challenge', 'Our System', 'Your Revenue', 'Technology', 'Contact'].map((link) =>
              <li key={link}>
                  <a
                  href={`#${link.toLowerCase().replace(' ', '-')}`}
                  className="text-slate-400 hover:text-white transition-colors">

                    {link}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-manrope text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="!text-base text-slate-400">dsotomayor@synergydds.net</li>
              <li className="text-slate-400">818-254-8353</li>
              <li className="text-slate-400">Nationwide Service</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} Synergy DDS. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>);

};

// Main App
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Navigation />
      <main>
        <HeroSection />
        <ProblemSolutionSection />
        <SynergySystemSection />
        <RevenueSection />
        <TechnologySection />
        <ContactSection />
      </main>
      <Footer />
    </div>);

}

export default App;